/**
 * PublicTimeline — premium vertical chronological timeline ordered by date_field.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../theme/ThemeContext';
import { getCurrencyDisplay } from '../../utils/currencies';
import { localeDateIn, parseInstant, toDisplayString, zoneOf } from '../../utils/utils';
import JsonCell from '../common/JsonCell';
import PublicTagBadges from './PublicTagBadges';

const BADGE_COLOR_MAP = {
  primary: '#2d6cf6', success: '#16a34a', warning: '#d97706',
  danger: '#dc2626', info: '#0891b2', secondary: '#64748b',
};

const TITLE_LIMIT = 60;
const SUBTITLE_LIMIT = 80;
const DESC_LIMIT = 140;

function trunc(val, limit) {
  const s = val == null ? '' : String(val);
  return s.length > limit ? s.slice(0, limit) + '…' : s;
}

export default function PublicTimeline({ records = [], mapping = {}, layoutConfig = {}, detailEnabled = true, detailButtonLabel = '', tableSlug: tableSlugProp }) {
  const navigate = useNavigate();
  const { tableSlug: tableSlugParam } = useParams();
  const tableSlug = tableSlugProp || tableSlugParam;
  const { theme } = useTheme();

  const accentColor = theme.primary;
  const badgeColors = mapping.badge_colors || {};
  const order = layoutConfig.timeline_order || 'desc';
  const showDateLabel = layoutConfig.show_date_label !== 'false';

  if (!mapping.date_field) {
    return (
      <div style={{
        background: `linear-gradient(135deg, #fff 0%, ${accentColor}06 100%)`, borderRadius: 14,
        border: `1.5px solid ${accentColor}30`,
        boxShadow: `0 2px 8px ${accentColor}14`,
        padding: '1rem 1.25rem', color: '#64748b', fontSize: '0.875rem',
      }}>
        Timeline requires a date field to be mapped. Please configure the public view.
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: '4rem 2rem',
        background: '#fff', borderRadius: 16,
        boxShadow: `0 2px 10px rgba(0,0,0,0.07), 0 6px 28px ${accentColor}18`,
        border: `1.5px solid ${accentColor}28`,
      }}>
        <div style={{
          width: 72, height: 72, borderRadius: '50%',
          background: `${accentColor}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, margin: '0 auto 16px',
          boxShadow: `0 4px 16px ${accentColor}18`,
        }}>📅</div>
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>No records yet.</p>
      </div>
    );
  }

  const sorted = [...records].sort((a, b) => {
    const da = parseInstant(a[mapping.date_field]) || new Date(0);
    const db = parseInstant(b[mapping.date_field]) || new Date(0);
    return order === 'asc' ? da - db : db - da;
  });

  return (
    <div style={{ position: 'relative', paddingLeft: 40 }}>
      <style>{`
        @keyframes timelineDotPulse {
          0%, 100% { box-shadow: 0 0 0 3px ${accentColor}, 0 0 0 6px ${accentColor}28; }
          50%       { box-shadow: 0 0 0 3px ${accentColor}, 0 0 0 11px ${accentColor}00; }
        }
        .arcee-pub__title    { font-weight: 700; font-size: 0.98rem; line-height: 1.35; color: #0f172a; }
        .arcee-pub__subtitle { font-size: 0.81rem; color: #334155; font-weight: 500; }
        .arcee-pub__desc     { font-size: 0.79rem; color: #64748b; line-height: 1.6; margin-top: 1px; }
        .arcee-pub__badge {
          display: inline-block;
          background: var(--arcee-pub-badge-bg, transparent);
          color: var(--arcee-pub-badge-color, inherit);
          border: 1.5px solid var(--arcee-pub-badge-border, transparent);
          font-size: 0.65rem; font-weight: 700;
          padding: 3px 10px; border-radius: 999px;
          letter-spacing: 0.05em; text-transform: uppercase;
        }
        .arcee-pub__date {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 0.69rem; font-weight: 700;
          color: var(--arcee-pub-date-color, inherit);
          background: var(--arcee-pub-date-bg, transparent);
          padding: 3px 11px; border-radius: 999px;
          border: 1.5px solid var(--arcee-pub-date-border, transparent);
          letter-spacing: 0.02em;
        }
        .arcee-pub__cost {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 0.69rem; font-weight: 700;
          color: #16a34a; background: #16a34a0d;
          padding: 3px 11px; border-radius: 999px;
          border: 1.5px solid #16a34a28;
          letter-spacing: 0.02em;
        }
        .arcee-pub__cta {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 0.7rem; font-weight: 700;
          color: #fff; background: ${accentColor};
          padding: 4px 11px; border-radius: 999px;
          box-shadow: 0 2px 8px ${accentColor}44;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
      `}</style>

      {/* Vertical connector line */}
      <div style={{
        position: 'absolute', left: 12, top: 10, bottom: 10, width: 4,
        background: `linear-gradient(to bottom, ${accentColor} 0%, ${accentColor}44 100%)`,
        borderRadius: 3,
      }} />

      {sorted.map((record, idx) => {
        const title = record[mapping.title_field] ?? null;
        const subtitle = mapping.subtitle_field ? record[mapping.subtitle_field] : null;
        const desc = mapping.description_field ? record[mapping.description_field] : null;
        const tags = mapping.tags_field ? record[mapping.tags_field] : null;

        const titleStr = (title != null && title !== '') ? toDisplayString(title) : '';
        const subtitleStr = (subtitle != null && subtitle !== '') ? toDisplayString(subtitle) : null;
        const descStr = (desc != null && desc !== '') ? toDisplayString(desc) : null;
        const tagsStr = (tags != null && tags !== '') ? String(tags) : null;
        const subtitleIsJson = subtitle !== null && typeof subtitle === 'object';
        const descIsJson = desc !== null && typeof desc === 'object';
        const badge = mapping.badge_field ? record[mapping.badge_field] : null;
        const dateVal = record[mapping.date_field];
        const costVal = mapping.cost_field ? record[mapping.cost_field] : null;

        const badgeKey = badge ? (badgeColors[badge] || 'primary') : 'primary';
        const badgeBg = BADGE_COLOR_MAP[badgeKey] || BADGE_COLOR_MAP.primary;

        return (
          <div
            key={record.id}
            className="arcee-pub--timeline"
            data-record-id={record.id}
            style={{
              position: 'relative',
              marginBottom: idx < sorted.length - 1 ? 24 : 0,
              cursor: detailEnabled ? 'pointer' : 'default',
            }}
            onClick={detailEnabled ? () => navigate(`/p/${tableSlug}/${record.id}`) : undefined}
          >
            {/* Animated dot */}
            <div style={{
              position: 'absolute', left: -32, top: 16,
              width: 20, height: 20, borderRadius: '50%',
              background: '#fff',
              border: `3px solid ${accentColor}`,
              boxShadow: `0 0 0 3px ${accentColor}, 0 0 0 6px ${accentColor}28`,
              animation: 'timelineDotPulse 2.6s ease-in-out infinite',
              zIndex: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: accentColor,
              }} />
            </div>

            {/* Card */}
            <div
              style={{
                background: `#ffffff`,
                borderRadius: 16,
                border: `1.5px solid ${accentColor}2a`,
                borderLeft: `4px solid ${accentColor}`,
                boxShadow: `0 2px 10px rgba(0,0,0,0.07), 0 6px 28px ${accentColor}1c`,
                padding: '1rem 1.25rem',
                transition: 'box-shadow 0.26s cubic-bezier(0.4,0,0.2,1), transform 0.26s cubic-bezier(0.4,0,0.2,1), border-color 0.26s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = `0 12px 44px ${accentColor}34, 0 4px 16px rgba(0,0,0,0.10)`;
                e.currentTarget.style.transform = 'translateX(5px)';
                e.currentTarget.style.borderColor = `${accentColor}50`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = `0 2px 10px rgba(0,0,0,0.07), 0 6px 28px ${accentColor}1c`;
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.borderColor = `${accentColor}2a`;
              }}
            >
              {/* Date + Cost pills */}
              {(showDateLabel && dateVal) || (costVal != null && costVal !== '') ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                  {showDateLabel && dateVal && (
                    <span
                      className="arcee-pub__date"
                      style={{
                        '--arcee-pub-date-color': accentColor,
                        '--arcee-pub-date-bg': `${accentColor}14`,
                        '--arcee-pub-date-border': `${accentColor}35`,
                      }}
                    >
                      <i className="bi bi-calendar-event" style={{ fontSize: '0.62rem' }} />
                      {localeDateIn(parseInstant(dateVal), { year: 'numeric', month: 'long', day: 'numeric' }, zoneOf(record))}
                    </span>
                  )}
                  {costVal != null && costVal !== '' && (
                    <span className="arcee-pub__cost">
                      <span style={{ fontWeight: 700 }}>{getCurrencyDisplay(mapping.cost_currency)}</span>
                      {costVal}
                    </span>
                  )}
                </div>
              ) : null}

              {/* Content */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>

                {mapping.title_field && titleStr && (
                  <div
                    className="arcee-pub__title"
                    title={titleStr.length > TITLE_LIMIT ? titleStr : undefined}
                  >
                    {trunc(titleStr, TITLE_LIMIT)}
                  </div>
                )}

                {subtitleStr && (
                  <div
                    className="arcee-pub__subtitle"
                    title={!subtitleIsJson && subtitleStr.length > SUBTITLE_LIMIT ? subtitleStr : undefined}
                  >
                    {subtitleIsJson ? <JsonCell value={subtitle} /> : trunc(subtitleStr, SUBTITLE_LIMIT)}
                  </div>
                )}

                {descStr && (
                  <div
                    className="arcee-pub__desc"
                    title={!descIsJson && descStr.length > DESC_LIMIT ? descStr : undefined}
                  >
                    {descIsJson ? <JsonCell value={desc} /> : trunc(descStr, DESC_LIMIT)}
                  </div>
                )}

                {tagsStr && (
                  <div style={{ marginTop: 2 }}>
                    <PublicTagBadges value={tagsStr} bgColor={mapping.tag_bg_color} accentColor={accentColor} />
                  </div>
                )}

                {(badge || (detailEnabled && detailButtonLabel)) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    {badge && (
                      <span
                        className="arcee-pub__badge"
                        style={{
                          '--arcee-pub-badge-bg': `${badgeBg}14`,
                          '--arcee-pub-badge-color': badgeBg,
                          '--arcee-pub-badge-border': `${badgeBg}30`,
                        }}
                      >
                        {badge}
                      </span>
                    )}

                    {detailEnabled && detailButtonLabel && (
                      <span
                        className="arcee-pub__cta"
                        onMouseEnter={e => {
                          e.currentTarget.style.boxShadow = `0 4px 14px ${accentColor}66`;
                          e.currentTarget.style.transform = 'scale(1.04)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.boxShadow = `0 2px 8px ${accentColor}44`;
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {detailButtonLabel}
                        <i className="bi bi-arrow-right" style={{ fontSize: '0.6rem' }} />
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
